// The automations that actually run, drawn from the workflows in this repo and
// the scripts they call. Each graph is the real shape of a pipeline: the nodes
// are the steps the script performs, in order, and the schedule is the cron in
// .github/workflows.
//
// Rules for anything added here:
//   - Only pipelines that exist and run. Ideas go in `couldBuild`, not here.
//   - `schedule` matches the cron. If a workflow is turned off, say so in
//     `status` rather than quietly leaving it on the page.
//   - `runsOn` lists what it actually touches, so a reader can price it.
//
// Layout: nodes carry `col` and `row`; WorkflowCanvas places them. Keep a
// pipeline under about 12 nodes, or the canvas stops being readable on a phone.

export const automations = [
  {
    slug: 'lead-swarm',
    name: 'Lead intelligence swarm',
    schedule: 'Every 2 hours, plus a daily sweep',
    status: 'running',
    summary:
      'Watches the places buyers show up, scores each one against an ideal customer profile, writes the opening line, and files it. I read a list, not a feed.',
    replaces: 'Manual prospecting and a scattered list of tabs',
    runsOn: ['GitHub Actions', 'Apify', 'Gemini', 'Airtable'],
    source: '.github/workflows/swarm-social.yml, reddit-agent.yml',
    nodes: [
      { id: 'cron', label: 'Schedule', note: 'every 2 hours', kind: 'trigger', col: 0, row: 1 },
      { id: 'li', label: 'LinkedIn', note: 'posts and comments', kind: 'source', col: 1, row: 0 },
      { id: 'x', label: 'X', note: 'search terms', kind: 'source', col: 1, row: 1 },
      { id: 'rd', label: 'Reddit', note: 'target subreddits', kind: 'source', col: 1, row: 2 },
      { id: 'norm', label: 'Normalize', note: 'one shape, deduped', kind: 'step', col: 2, row: 1 },
      { id: 'score', label: 'Score fit', note: 'ICP match and reason', kind: 'model', col: 3, row: 1 },
      { id: 'gate', label: 'Above threshold?', kind: 'gate', col: 4, row: 1 },
      { id: 'draft', label: 'Draft the opener', note: 'specific to their post', kind: 'model', col: 5, row: 0 },
      { id: 'drop', label: 'Drop', note: 'with the reason kept', kind: 'end', col: 5, row: 2 },
      { id: 'file', label: 'File the lead', note: 'queue for review', kind: 'store', col: 6, row: 0 },
    ],
    edges: [
      ['cron', 'li'], ['cron', 'x'], ['cron', 'rd'],
      ['li', 'norm'], ['x', 'norm'], ['rd', 'norm'],
      ['norm', 'score'], ['score', 'gate'],
      ['gate', 'draft', 'pass'], ['gate', 'drop', 'no'],
      ['draft', 'file'],
    ],
  },
  {
    slug: 'content-pipeline',
    name: 'Content pipeline',
    schedule: 'Trend scan twice daily, posts on a weekday schedule',
    status: 'running',
    summary:
      'Reads what is moving in the sources I care about, picks one topic, drafts around it, and queues the social versions. The draft is mine to approve, not the machine\u2019s to publish.',
    replaces: 'A content calendar nobody fills in',
    runsOn: ['GitHub Actions', 'Gemini', 'Airtable', 'LinkedIn API'],
    source: '.github/workflows/trend-scanner.yml, x-content-gen.yml, linkedin-poster.yml',
    nodes: [
      { id: 'cron', label: 'Schedule', note: 'twice daily', kind: 'trigger', col: 0, row: 1 },
      { id: 'hn', label: 'Hacker News', kind: 'source', col: 1, row: 0 },
      { id: 'gh', label: 'GitHub trending', kind: 'source', col: 1, row: 1 },
      { id: 'ph', label: 'Product Hunt', kind: 'source', col: 1, row: 2 },
      { id: 'rank', label: 'Rank topics', note: 'what is worth a post', kind: 'model', col: 2, row: 1 },
      { id: 'draft', label: 'Draft', note: 'long form plus social cuts', kind: 'model', col: 3, row: 1 },
      { id: 'review', label: 'Human review', note: 'pull request', kind: 'gate', col: 4, row: 1 },
      { id: 'blog', label: 'Publish', note: 'on merge', kind: 'store', col: 5, row: 0 },
      { id: 'queue', label: 'Queue social', note: 'LinkedIn on weekdays', kind: 'store', col: 5, row: 2 },
    ],
    edges: [
      ['cron', 'hn'], ['cron', 'gh'], ['cron', 'ph'],
      ['hn', 'rank'], ['gh', 'rank'], ['ph', 'rank'],
      ['rank', 'draft'], ['draft', 'review'],
      ['review', 'blog', 'approved'], ['review', 'queue', 'approved'],
    ],
  },
  {
    slug: 'nurture-loop',
    name: 'Nurture loop',
    schedule: 'Hourly',
    status: 'running',
    summary:
      'Follows up on its own. It checks what each lead has done since the last touch, writes the next message in context, and stops the moment a human replies.',
    replaces: 'Reminders you set and then ignore',
    runsOn: ['GitHub Actions', 'Gemini', 'Airtable', 'Reddit API'],
    source: '.github/workflows/lead-nurture.yml, reddit-monitor.yml',
    nodes: [
      { id: 'cron', label: 'Schedule', note: 'hourly', kind: 'trigger', col: 0, row: 1 },
      { id: 'due', label: 'Who is due', note: 'by last touch', kind: 'step', col: 1, row: 1 },
      { id: 'replied', label: 'Replied since?', kind: 'gate', col: 2, row: 1 },
      { id: 'stop', label: 'Hand to me', note: 'sequence stops', kind: 'end', col: 3, row: 0 },
      { id: 'ctx', label: 'Gather context', note: 'their thread and activity', kind: 'step', col: 3, row: 2 },
      { id: 'write', label: 'Write follow up', kind: 'model', col: 4, row: 2 },
      { id: 'temp', label: 'Update temperature', kind: 'store', col: 5, row: 2 },
      { id: 'kpi', label: 'Weekly summary', kind: 'store', col: 6, row: 2 },
    ],
    edges: [
      ['cron', 'due'], ['due', 'replied'],
      ['replied', 'stop', 'yes'], ['replied', 'ctx', 'no'],
      ['ctx', 'write'], ['write', 'temp'], ['temp', 'kpi'],
    ],
  },
  {
    slug: 'outreach-pipeline',
    name: 'Grounded outreach pipeline',
    schedule: 'On demand',
    status: 'running',
    summary:
      'Scores a role against evidence of what I have actually built, finds the person who owns it, and refuses to send anything the evidence does not support. Published addresses only: guessed ones delivered 25 percent of the time, published ones 90 to 97.',
    replaces: 'Spray and pray sequences',
    runsOn: ['Python CLI', 'Apify', 'Claude', 'Gmail'],
    source: 'jobhunter, a separate repo',
    caseStudy: 'jobhunter',
    nodes: [
      { id: 'in', label: 'Roles in', note: 'scraped listings', kind: 'trigger', col: 0, row: 1 },
      { id: 'retr', label: 'Hybrid retrieval', note: 'keyword, dense, tags', kind: 'step', col: 1, row: 1 },
      { id: 'fit', label: 'Fit score', note: 'evidence weighted', kind: 'model', col: 2, row: 1 },
      { id: 'contact', label: 'Find the owner', kind: 'step', col: 3, row: 0 },
      { id: 'verify', label: 'Address published?', kind: 'gate', col: 4, row: 0 },
      { id: 'hold', label: 'Hold', note: 'never guess', kind: 'end', col: 5, row: 2 },
      { id: 'write', label: 'Draft in my voice', kind: 'model', col: 5, row: 0 },
      { id: 'lint', label: 'Voice and claim lint', note: 'blocks unsupported lines', kind: 'gate', col: 6, row: 0 },
      { id: 'approve', label: 'My approval', kind: 'gate', col: 7, row: 0 },
      { id: 'send', label: 'Send and record', kind: 'store', col: 8, row: 0 },
    ],
    edges: [
      ['in', 'retr'], ['retr', 'fit'], ['fit', 'contact'],
      ['contact', 'verify'], ['verify', 'hold', 'no'], ['verify', 'write', 'yes'],
      ['write', 'lint'], ['lint', 'hold', 'fails'], ['lint', 'approve', 'passes'], ['approve', 'send'],
    ],
  },
  {
    slug: 'site-integrity',
    name: 'Site integrity checks',
    schedule: 'Daily, and on every pull request',
    status: 'running',
    summary:
      'The site checks its own claims. Every linked project gets opened once a day, and a pull request cannot merge if it breaks the page weight budget or the writing rules.',
    replaces: 'Finding out from a visitor',
    runsOn: ['GitHub Actions'],
    source: '.github/workflows/status-check.yml, media-budget.yml, blog-check.yml',
    nodes: [
      { id: 'cron', label: 'Daily', kind: 'trigger', col: 0, row: 0 },
      { id: 'pr', label: 'Pull request', kind: 'trigger', col: 0, row: 2 },
      { id: 'ping', label: 'Open each project', kind: 'step', col: 1, row: 0 },
      { id: 'status', label: 'Write status file', note: 'the Live badge', kind: 'store', col: 2, row: 0 },
      { id: 'weight', label: 'Page weight budget', kind: 'gate', col: 1, row: 2 },
      { id: 'lint', label: 'Writing rules', kind: 'gate', col: 2, row: 2 },
      { id: 'block', label: 'Block the merge', kind: 'end', col: 3, row: 2 },
    ],
    edges: [
      ['cron', 'ping'], ['ping', 'status'],
      ['pr', 'weight'], ['weight', 'lint', 'passes'], ['lint', 'block', 'fails'],
    ],
  },
];

// Things I have not built and will not pretend to have. These are the shapes I
// build for other people most often.
export const couldBuild = [
  {
    title: 'Inbox triage',
    body: 'Sort what arrives, draft the replies that are obvious, and put everything else in front of a person with the context attached.',
  },
  {
    title: 'Quote and proposal assembly',
    body: 'Pull the numbers from the systems that own them, build the document, and route it for one approval instead of five emails.',
  },
  {
    title: 'Reporting that writes itself',
    body: 'A weekly summary drawn from the same source the dashboard uses, with the figures traced to where they came from.',
  },
  {
    title: 'Onboarding a customer',
    body: 'Accounts created, access granted, welcome sequence started, and a checklist someone can see rather than guess at.',
  },
];
